import { styled, keyframes } from "@mui/material/styles";
import { Box, Button } from "@mui/material";

const gradientBorder = keyframes`
  0% {
    border-color: #FF6B6B;
  }
  16.66% {
    border-color: #4ECDC4;
  }
  33.33% {
    border-color: #45B7D1;
  }
  50% {
    border-color: #96CEB4;
  }
  66.66% {
    border-color: #FFEAA7;
  }
  83.33% {
    border-color: #DDA0DD;
  }
  100% {
    border-color: #FF6B6B;
  }
`;

export const TabContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-evenly",
  width: "90%",
  height: "86px",
  gap: "16px",
  padding: "8px",
  margin: "10px 0px 0px 20px",
  backgroundColor: theme.palette.jigsaw.white,
  boxShadow: "5px 5px 10px rgba(0, 0, 0, 0.1)",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.jigsaw.green}`,
}));

export const TabButton = styled(Button, {
  shouldForwardProp: (prop) =>
    !["active", "isFinalizedSummary", "isProductComparison", "isSourceDocuments", "isClickable", "hasUserHovered"].includes(prop),
})(({ theme, active, isFinalizedSummary, isProductComparison, isSourceDocuments, isClickable, hasUserHovered }) => {
  return {
    display: "flex",
    alignItems: "center",
    padding: "8px 16px",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    color: active ? theme.palette.jigsaw.green : theme.palette.grey[500],
    backgroundColor: "transparent",
    fontWeight: active ? 600 : "normal",
    position: "relative",
    
    // Active tab styling
    ...(active && {
      border: `1px solid ${theme.palette.jigsaw.green}`,
      color: theme.palette.jigsaw.green,
      backgroundColor: "transparent",
    }),
    
    // Override disabled styling for active tabs
    "&.Mui-disabled": {
      ...(active && {
        color: `${theme.palette.jigsaw.green} !important`,
        border: `1px solid ${theme.palette.jigsaw.green} !important`,
        backgroundColor: "transparent !important",
      }),
    },
    
    // Gradient border animation for clickable Product Comparison tab
    ...(isProductComparison && isClickable && !active && {
      color: "black",
      fontWeight: 500,
      border: "1px solid #FF6B6B",
      ...(!hasUserHovered && {
        animation: `${gradientBorder} 3s ease infinite`,
      }),
      ...(hasUserHovered && {
        borderColor: theme.palette.primary.main,
      }),
      "&:hover": {
        color: "black",
        borderColor: theme.palette.primary.main,
      },
    }),

    // Gradient border animation for clickable Finalized Summary tab
    ...(isFinalizedSummary && isClickable && !active && {
      color: "black",
      fontWeight: 500,
      border: "1px solid #FF6B6B",
      ...(!hasUserHovered && {
        animation: `${gradientBorder} 3s ease infinite`,
      }),
      ...(hasUserHovered && {
        borderColor: theme.palette.primary.main,
      }),
      "&:hover": {
        color: "black",
        borderColor: theme.palette.primary.main,
      },
    }),

    // Gradient border animation for clickable Source Documents tab
    ...(isSourceDocuments && isClickable && {
      color: "black",
      fontWeight: 500,
      border: "1px solid #FF6B6B",
      ...(!hasUserHovered && {
        animation: `${gradientBorder} 3s ease infinite`,
      }),
      ...(hasUserHovered && {
        borderColor: theme.palette.primary.main,
      }),
      "&:hover": {
        color: "black",
        borderColor: theme.palette.primary.main,
      },
    }),
    
    "&:hover": {
      color: theme.palette.grey[700],
    },
  };
});

export const TabIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  marginRight: "10px",
  display:"flex",
  color: active ? theme.palette.warning.main : theme.palette.grey[400],
}));

export const TabImage = styled("img")(() => ({
  width: 16,
  height: 16,
  objectFit: "contain",
}));

export const ActiveIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  display: active ? "block" : "none",
  borderRadius: "0 2px 2px 0",
  color: theme.palette.jigsaw.green,
  backgroundColor: theme.palette.jigsaw.green,
  width: "5px",
  height: "20px",
  margin: "auto 0",
  alignSelf: "flex-start",
  position: "absolute",
  left: 0,
}));
